<script setup lang="ts">
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { api, type RoleShareReview, refusalCode, requestFailure } from '@/api'
import RoleListingTasks from '@/components/RoleListingTasks.vue'
import { initiallyTicked, pendingWrites, refusalSentence, saveListing } from '@/lib/catalogListing'

/**
 * Publishing a role to the catalog, or updating what it lists once it is there. The dialog reads the role's listing
 * (`GET /v1/catalog/share/roles/:id`): each task that runs as the role, ticked when it is in the catalog or not yet
 * decided on, and the strays still on the role. Publishing puts the role's name in the catalog first, then saves the
 * ticks one task at a time (`saveListing`), so a ticked task is listed on the role and an unticked one stays private.
 * A refused task is said beside it, the rest still save, and the dialog stays open so the person can fix it.
 */
const props = defineProps<{ roleId: string; open: boolean }>()
const emit = defineEmits<{
    'update:open': [open: boolean]
    /** The role's listing was saved: the review as it stands after. */
    saved: [review: RoleShareReview]
}>()

const review = ref<RoleShareReview | null>(null)
const loadFailed = ref(false)
const ticked = ref<Set<string>>(new Set())
const busy = ref(false)
const status = ref('')
const error = ref('')
const errors = ref<Record<string, string>>({})
const planRefused = ref(false)
const nameTaken = ref(false)

const isPublic = computed(() => review.value?.catalog.public === true)
const heldElsewhere = computed(() => review.value?.catalog.heldElsewhere === true)
const writes = computed(() => (review.value ? pendingWrites(review.value.tasks, ticked.value, review.value.strays) : 0))
const ready = computed(
    () =>
        review.value !== null &&
        !busy.value &&
        !planRefused.value &&
        !heldElsewhere.value &&
        (!isPublic.value || writes.value > 0),
)

async function load(): Promise<void> {
    review.value = null
    loadFailed.value = false
    error.value = ''
    status.value = ''
    errors.value = {}
    planRefused.value = false
    nameTaken.value = false
    try {
        const loaded = await api.roleShareReview(props.roleId)
        review.value = loaded
        planRefused.value = !loaded.canPublish
        ticked.value = initiallyTicked(loaded.tasks)
    } catch {
        loadFailed.value = true
    }
}

watch(
    () => props.open,
    open => {
        if (open) void load()
    },
    { immediate: true },
)

function close(): void {
    if (!busy.value) emit('update:open', false)
}

function toggle(taskId: string, on: boolean): void {
    const next = new Set(ticked.value)
    if (on) next.add(taskId)
    else next.delete(taskId)
    ticked.value = next
}

/** A refused role publish in the page's words: the plan and a taken name; anything else in the server's. */
function roleRefusal(caught: unknown): string {
    const code = refusalCode(caught)
    if (requestFailure(caught)?.status === 402 || code === 'catalog_publish_not_in_plan') {
        planRefused.value = true
        return 'Your plan does not include catalog publishing.'
    }
    if (
        code === 'catalog_role_slug_taken' ||
        (caught instanceof Error && caught.message === 'catalog_role_slug_taken')
    ) {
        nameTaken.value = true
        return 'Another publisher already holds this role’s name in the catalog. Rename the role, then publish it.'
    }
    return refusalSentence(caught, 'Could not publish the role.')
}

async function save(): Promise<void> {
    const loaded = review.value
    if (!loaded || !ready.value) return
    busy.value = true
    error.value = ''
    errors.value = {}
    try {
        if (!loaded.catalog.public) {
            status.value = `Publishing ${loaded.role.name}…`
            try {
                await api.publishRole(loaded.role.id)
            } catch (caught) {
                error.value = roleRefusal(caught)
                return
            }
        }
        const refused = await saveListing(loaded.tasks, ticked.value, loaded.strays, sentence => {
            status.value = sentence
        })
        const after = await api.roleShareReview(props.roleId)
        emit('saved', after)
        if (Object.keys(refused).length > 0) {
            review.value = after
            ticked.value = initiallyTicked(after.tasks)
            errors.value = refused
            error.value = 'Some changes could not be saved. Fix what each says, or untick it, and save again.'
            return
        }
        emit('update:open', false)
    } catch (caught) {
        error.value = refusalSentence(caught, 'Could not save the listing.')
    } finally {
        status.value = ''
        busy.value = false
    }
}
</script>

<template>
    <Dialog :open="open" @update:open="value => { if (!value) close() }">
        <DialogContent class="max-h-4/5 overflow-y-auto sm:max-w-2xl" data-testid="role-publish-dialog">
            <DialogHeader>
                <DialogTitle>
                    {{ isPublic ? 'Update' : 'Publish' }} {{ review?.role.name ?? 'this role' }}
                    {{ isPublic ? 'in' : 'to' }} the catalog
                </DialogTitle>
                <DialogDescription>
                    The role’s name and the tasks you tick go to the catalog, where any company can clone them. Its
                    members, connected accounts and memory stay with you.
                </DialogDescription>
            </DialogHeader>
            <p v-if="loadFailed" class="text-destructive text-sm" role="alert">
                Could not read the role’s tasks. Close this and try again.
            </p>
            <p v-else-if="!review" class="text-muted-foreground text-sm">Reading the role…</p>
            <div v-else class="space-y-4">
                <p v-if="planRefused" class="text-sm" role="alert" data-testid="role-publish-plan">
                    Your plan does not include catalog publishing.
                    <RouterLink class="underline" to="/billing#plans">See plans</RouterLink>
                </p>
                <p v-if="heldElsewhere" class="text-destructive text-sm" role="alert">
                    Another publisher already has a public role with this name.
                    <RouterLink class="underline" to="/chat">Ask in Chat to rename it</RouterLink>, then publish it.
                </p>
                <section class="space-y-2" aria-labelledby="role-publish-tasks-heading">
                    <ZhSectionHeading id="role-publish-tasks-heading" as="h3">Tasks</ZhSectionHeading>
                    <p class="text-muted-foreground text-sm" data-testid="role-publish-tasks">
                        Ticked tasks are listed with the role. An unticked task stays private, and is withdrawn if it
                        is in the catalog now.
                    </p>
                    <RoleListingTasks
                        id-prefix="role-publish"
                        :tasks="review.tasks"
                        :strays="review.strays"
                        :ticked="ticked"
                        :role-public="isPublic"
                        :disabled="busy"
                        :errors="errors"
                        @toggle="toggle"
                    />
                </section>
                <p v-if="!isPublic" class="text-sm" data-testid="role-publish-final">
                    You can’t withdraw the role from the catalog here once it is published.
                </p>
                <p v-else-if="writes === 0" class="text-muted-foreground text-sm" data-testid="role-publish-current">
                    The catalog is up to date with this role.
                </p>
                <p v-if="status" class="text-sm" role="status" data-testid="role-publish-status">{{ status }}</p>
                <p v-if="error" class="text-destructive text-sm" role="alert" data-testid="role-publish-error">
                    {{ error }}
                    <RouterLink v-if="nameTaken" class="underline" to="/chat">Ask in Chat to rename it</RouterLink>
                </p>
            </div>
            <DialogFooter>
                <Button type="button" variant="outline" :disabled="busy" @click="close">Cancel</Button>
                <Button type="button" data-testid="role-publish-confirm" :disabled="!ready" @click="save">
                    {{ busy ? 'Saving…' : isPublic ? 'Update catalog' : 'Publish' }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
